import {
  Wallet, Gauge, TrendingUp, CalendarDays, Target, Scale, TrendingDown,
  ShieldAlert, Hash, Sigma, MoveUpRight, Percent, LineChart, BarChart3,
  AreaChart, PieChart, BarChartHorizontal, Clock3, ListOrdered, Layers,
  NotebookPen, Users, GitBranch, Columns2,
} from "lucide-react";

// Every widget follows the same 3-part contract:
// (1) data shape it needs, (2) how it renders, (3) settings it exposes (account + time range).
export const WIDGETS = {
  // ---------------- stats ----------------
  balance: { name: "Balance", category: "stats", icon: Wallet, w: 2, h: 2, minW: 2, minH: 2,
    desc: "Your account balance right now, like checking your bank account." },
  equity: { name: "Equity", category: "stats", icon: Gauge, w: 2, h: 2, minW: 2, minH: 2,
    desc: "Balance plus the profit or loss of trades still open." },
  total_gain: { name: "Total Gain", category: "stats", icon: TrendingUp, w: 2, h: 2, minW: 2, minH: 2,
    desc: "How much the account has grown since day one, as a percentage." },
  monthly_profit: { name: "Monthly Profit", category: "stats", icon: CalendarDays, w: 2, h: 2, minW: 2, minH: 2,
    desc: "Money made or lost so far this calendar month." },
  win_rate: { name: "Win Rate", category: "stats", icon: Target, w: 2, h: 2, minW: 2, minH: 2,
    desc: "The percentage of trades that closed in profit." },
  profit_factor: { name: "Profit Factor", category: "stats", icon: Scale, w: 2, h: 2, minW: 2, minH: 2,
    desc: "Dollars won for every dollar lost. Above 1 means profitable." },
  max_drawdown: { name: "Max Drawdown", category: "stats", icon: TrendingDown, w: 2, h: 2, minW: 2, minH: 2,
    desc: "The deepest dip from a peak. Your worst losing stretch." },
  risk_level: { name: "Risk Level", category: "stats", icon: ShieldAlert, w: 2, h: 2, minW: 2, minH: 2,
    desc: "A simple 1–5 gauge of how risky this account trades." },
  total_trades: { name: "Total Trades", category: "stats", icon: Hash, w: 2, h: 2, minW: 2, minH: 2,
    desc: "How many trades have been closed, with longs vs shorts." },
  avg_win_loss: { name: "Avg Win / Loss", category: "stats", icon: Sigma, w: 2, h: 2, minW: 2, minH: 2,
    desc: "The average winning trade next to the average losing one." },
  pips_won: { name: "Pips", category: "stats", icon: MoveUpRight, w: 2, h: 2, minW: 2, minH: 2,
    desc: "Total pips captured across all closed trades." },
  expectancy: { name: "Expectancy", category: "stats", icon: Percent, w: 2, h: 2, minW: 2, minH: 2,
    desc: "What the average trade is worth in dollars." },
  // ---------------- charts ----------------
  equity_curve: { name: "Equity Curve", category: "charts", icon: LineChart, w: 6, h: 4, minW: 3, minH: 3,
    desc: "Account growth over time. The chart that tells the whole story." },
  monthly_performance: { name: "Monthly Performance", category: "charts", icon: BarChart3, w: 6, h: 4, minW: 3, minH: 3,
    desc: "Gain or loss for each month, side by side." },
  drawdown_chart: { name: "Drawdown", category: "charts", icon: AreaChart, w: 6, h: 4, minW: 3, minH: 3,
    desc: "How far below its peak the account was at any moment." },
  symbol_breakdown: { name: "Symbol Breakdown", category: "charts", icon: PieChart, w: 4, h: 4, minW: 3, minH: 3,
    desc: "Which markets make (or lose) the money." },
  win_loss_distribution: { name: "Win/Loss Distribution", category: "charts", icon: BarChartHorizontal, w: 6, h: 4, minW: 3, minH: 3,
    desc: "A histogram of trade results. The shape of your strategy." },
  trading_hours: { name: "Trading Hours", category: "charts", icon: Clock3, w: 6, h: 4, minW: 3, minH: 3,
    desc: "When trades happen and which hours actually pay." },
  // ---------------- lists ----------------
  recent_trades: { name: "Recent Trades", category: "lists", icon: ListOrdered, w: 6, h: 4, minW: 3, minH: 3,
    desc: "The latest closed trades, like a transaction history." },
  open_positions: { name: "Open Positions", category: "lists", icon: Layers, w: 6, h: 4, minW: 3, minH: 3,
    desc: "Trades currently running and their floating profit." },
  journal: { name: "Journal", category: "lists", icon: NotebookPen, w: 4, h: 4, minW: 3, minH: 3,
    desc: "Your latest trading notes and reflections." },
  accounts_list: { name: "Accounts Overview", category: "lists", icon: Users, w: 6, h: 4, minW: 3, minH: 3,
    desc: "Every connected account with its performance beside it." },
  version_history: { name: "Version History", category: "lists", icon: GitBranch, w: 4, h: 4, minW: 3, minH: 3,
    desc: "What changed in your strategy or EA, and when." },
  account_comparison: { name: "Account Comparison", category: "lists", icon: Columns2, w: 6, h: 4, minW: 3, minH: 3,
    desc: "Two accounts side by side across the key stats." },
};

export const CATEGORIES = [
  { id: "stats", label: "Stats" },
  { id: "charts", label: "Charts" },
  { id: "lists", label: "Lists" },
];

export function widgetMeta(type) {
  return WIDGETS[type] || { name: type, category: "stats", icon: Gauge, w: 3, h: 2, minW: 2, minH: 2, desc: "" };
}
